import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Stack,
    Alert,
    Button,
    Grid,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSellerPageContext } from '../../context-state/SellerPageContext.jsx';
import { useLocales } from '../../locales';
import useResponsive from '../../hooks/minimal/useResponsive.jsx';
import * as API from '../../api';
import { getEndUserTokenKeyByHandler } from '../../utils/endUser.js';
import { LinearLoader } from '../../components/linear-loader/LinearLoader';
import Iconify from '../../components/minimal/iconify';
import FamilyMemberCard from './FamilyMemberCard.jsx';
import CompleteFamilyProfileDialog from './CompleteFamilyProfileDialog.jsx';

const ManageFamilyPage = () => {
    const { handler, subscriptionId } = useParams();
    const navigate = useNavigate();
    const { accountContext, subscriptionContext, endUserContext } = useSellerPageContext();
    const { account } = accountContext;
    const { subscriptions } = subscriptionContext;
    const { endUser } = endUserContext;
    const { translate } = useLocales();
    const isMobile = useResponsive('down', 'sm');

    const [familyMembers, setFamilyMembers] = useState([]);
    const [familyRequiredFields, setFamilyRequiredFields] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [autoOpenedFromQuery, setAutoOpenedFromQuery] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const token = localStorage.getItem(getEndUserTokenKeyByHandler(handler));

    // Find the subscription template for this subscription
    const parentSubscription = endUser
        ? (endUserContext.purchasedSubscriptions || []).find((s) => s._id === subscriptionId)
        : null;

    // Find the subscription template (for familyChildMaxAge etc.)
    const subscriptionTemplate = subscriptions
        ? subscriptions.find((s) => s._id === parentSubscription?.subscriptionType)
        : null;

    const fetchFamilyMembers = async () => {
        const marketplaceId = account?.marketPlace?._id;
        if (!token || !subscriptionId || !account?._id || !marketplaceId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const sellerId = account._id;
            const { data } = await API.getFamilyMembers({ marketplaceId, sellerId, parentSubscriptionId: subscriptionId, token });
            // API returns { members, familyRequiredFields } or plain array (backwards compat)
            if (Array.isArray(data)) {
                setFamilyMembers(data);
            } else {
                setFamilyMembers(data?.members || []);
                setFamilyRequiredFields(data?.familyRequiredFields || null);
            }
        } catch (error) {
            console.error('Error fetching family members:', error);
            setFamilyMembers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFamilyMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionId, token, account?._id, account?.marketPlace?._id]);

    // Auto-open a specific member's edit dialog when arriving via ?openMember=<id>
    // (used by the IncompleteProfilesChecklist on my-account so clicking "Manage"
    // next to a family member jumps straight to their dialog).
    useEffect(() => {
        if (loading || autoOpenedFromQuery) return;
        const memberIdToOpen = searchParams.get('openMember');
        if (!memberIdToOpen) return;
        const target = familyMembers.find((m) => m._id?.toString() === memberIdToOpen);
        if (target) {
            setSelectedMember(target);
            setDialogOpen(true);
            setAutoOpenedFromQuery(true);
            const next = new URLSearchParams(searchParams);
            next.delete('openMember');
            setSearchParams(next, { replace: true });
        }
        // intentionally narrow deps to avoid re-running on our own setSearchParams
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, familyMembers]);

    const handleComplete = (member) => {
        setSelectedMember(member);
        setDialogOpen(true);
    };

    const handleEdit = (member) => {
        setSelectedMember(member);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedMember(null);
    };

    const handleDialogSuccess = () => {
        setDialogOpen(false);
        setSelectedMember(null);
        fetchFamilyMembers();
    };

    const incompleteCount = familyMembers.filter((m) => !m.isProfileComplete).length;

    const adultMembers = familyMembers.filter((m) => m.familyRole === 'adult');
    const childMembers = familyMembers.filter((m) => m.familyRole === 'child');

    const compositionParts = [];
    if (adultMembers.length > 0) {
        compositionParts.push(`${adultMembers.length} ${translate('family.adults')}`);
    }
    if (childMembers.length > 0) {
        compositionParts.push(`${childMembers.length} ${translate('family.children')}`);
    }
    const compositionSummary = compositionParts.join(', ');

    if (loading) {
        return <LinearLoader />;
    }

    return (
        <>
            <Helmet>
                <title>{account?.name || ''}</title>
            </Helmet>

            <Container maxWidth="md" sx={{ py: isMobile ? 2 : 4 }}>
                <Stack spacing={3}>
                    {/* Back link */}
                    <Button
                        startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                        onClick={() => navigate(`/seller/${handler}/my-account`)}
                        sx={{ alignSelf: 'flex-start', px: 0 }}
                        color="inherit"
                    >
                        {translate('family.back_to_my_account')}
                    </Button>

                    {/* Header */}
                    <Stack spacing={0.5}>
                        <Typography variant="h4">
                            {translate('family.family_members')}
                        </Typography>
                        {compositionSummary && (
                            <Typography variant="body2" color="text.secondary">
                                {compositionSummary}
                            </Typography>
                        )}
                    </Stack>

                    {/* Incomplete warning banner */}
                    {incompleteCount > 0 && (
                        <Alert severity="warning">
                            {translate('family.incomplete_warning', { count: incompleteCount })}
                        </Alert>
                    )}

                    {/* Family member cards */}
                    <Grid container spacing={2}>
                        {/* Family members (excluding the parent/owner) */}
                        {familyMembers.map((member) => (
                            <Grid item xs={12} sm={6} key={member._id}>
                                <FamilyMemberCard
                                    member={member}
                                    endUser={member.endUser}
                                    isSelf={false}
                                    onComplete={handleComplete}
                                    onEdit={handleEdit}
                                    sellerId={account?._id}
                                    subscriptionName={parentSubscription?.name}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {familyMembers.length === 0 && !loading && (
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                            {translate('family.no_family_members')}
                        </Typography>
                    )}
                </Stack>
            </Container>

            {/* Complete/Edit profile dialog */}
            <CompleteFamilyProfileDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
                member={selectedMember}
                handler={handler}
                token={token}
                subscriptionTemplate={subscriptionTemplate}
                account={account}
                familyRequiredFields={familyRequiredFields}
            />
        </>
    );
};

export default ManageFamilyPage;
